/**
 * Hook de autenticación para acceder a datos del usuario actual.
 * Proporciona información del usuario y roles para autorización en UI.
 *
 * @taskId T473
 * @see HU-27-FE
 */

import { useState } from 'react';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const UNAUTHENTICATED_STATE: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
};

function readAuthState(): AuthState {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return UNAUTHENTICATED_STATE;
    }

    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) {
      return UNAUTHENTICATED_STATE;
    }

    const payload = JSON.parse(atob(parts[1])) as {
      exp?: number;
      sub?: string;
      userId?: string;
      email?: string;
      name?: string;
      username?: string;
      roles?: string[];
      authorities?: string[];
    };

    if (payload.exp && payload.exp * 1000 < new Date().getTime()) {
      return UNAUTHENTICATED_STATE;
    }

    const user: AuthUser = {
      id: payload.sub || payload.userId || '',
      email: payload.email || '',
      name: payload.name || payload.username || '',
      roles: payload.roles || payload.authorities || [],
    };

    return { user, isAuthenticated: true, isLoading: false };
  } catch {
    return UNAUTHENTICATED_STATE;
  }
}

/**
 * Hook para obtener el estado de autenticación actual.
 * Lee del localStorage el token JWT y extrae info del usuario.
 */
export function useAuth(): AuthState {
  const [authState] = useState(readAuthState);
  return authState;
}
